import type { PermissionGuidance } from '@/types/permission'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { permissionService } from '@/services/PermissionService'
import { useUserStorev2 as useUserStore } from '@/store/modules/userCenter'
import { getSafeUserRole } from '@/utils/roleUtils'

function getFullPermissions(userStore: any) {
  const permissions = Array.isArray(userStore.permissions) ? userStore.permissions : []
  const role = getSafeUserRole(userStore.userInfo, userStore.isLoggedIn, !!userStore.token)
  return permissionService.getUserFullPermissions(role, permissions)
}

/**
 * ??????????
 */
export function usePermissionNavigation() {
  // const userStore = useUserStore()
  const route = useRoute()

  const navigationGroups = computed(() => {
    // const permissions = Array.isArray(userStore.permissions) ? userStore.permissions : []
    // ?????????????????????? sidebarNavigationService
    return []
  })

  const activeMenu = computed(() => route.path)

  return {
    navigationGroups,
    activeMenu,
  }
}

/**
 * ?????? Composable
 */
export function usePagePermission(path?: string) {
  const userStore = useUserStore()
  const route = useRoute()

  const hasAccess = computed(() => {
    const targetPath = path || route.path
    const permissions = getFullPermissions(userStore)
    return permissionService.hasPermission(targetPath, permissions)
  })

  return {
    hasAccess,
  }
}

/**
 * ?????? Composable
 */
export function useFunctionPermissions(functionKeys: string[], basePath?: string) {
  const userStore = useUserStore()
  const route = useRoute()

  const checkPermission = (key: string) => {
    const currentPath = basePath || route.path
    const permissionKey = `${currentPath}:${key}`
    const permissions = getFullPermissions(userStore)
    return permissionService.hasPermission(permissionKey, permissions)
  }

  const permissions = computed(() => {
    const result: Record<string, boolean> = {}
    functionKeys.forEach((key) => {
      result[key] = checkPermission(key)
    })
    return result
  })

  return {
    permissions,
    checkPermission,
  }
}

/**
 * ?????? Composable
 */
export function usePermissionDisplay() {
  const userStore = useUserStore()

  const can = (permission: string) => {
    const permissions = getFullPermissions(userStore)
    return permissionService.hasPermission(permission, permissions)
  }

  const canAny = (permissionsList: string[]) => {
    const permissions = getFullPermissions(userStore)
    return permissionsList.some(p => permissionService.hasPermission(p, permissions))
  }

  const canAll = (permissionsList: string[]) => {
    const permissions = getFullPermissions(userStore)
    return permissionsList.every(p => permissionService.hasPermission(p, permissions))
  }

  return {
    can,
    canAny,
    canAll,
  }
}

/**
 * ???? Composable
 */
export interface PermissionGuidanceOptions {
  /** ???????? */
  autoCheckRoute?: boolean
  /** ???????? */
  onDenied?: () => void
  /** ????????? */
  showDialog?: boolean
  /** ???????????? */
  autoNavigate?: boolean
  /** ???????? */
  showPreviewMode?: boolean
  /** ????????????? */
  rememberChoice?: boolean
}

export function usePermissionGuidance(options: PermissionGuidanceOptions = {}) {
  const { autoCheckRoute = true, onDenied, showDialog = false } = options

  const userStore = useUserStore()
  const route = useRoute()
  const router = useRouter()

  const showGuidanceDialog = ref(showDialog)
  const currentGuidance = ref<PermissionGuidance | undefined>(undefined)

  const guidanceConfig: Record<string, PermissionGuidance> = {
    '/user/finance': {
      title: '??????',
      description: '???????????????????????????',
      missingPermission: 'finance:access',
      benefitPreview: ['??????', '??????', '??????'],
      upgradePaths: [
        { role: 'designer', path: '/auth/verify/designer', benefits: '??????', time: '?2??' },
      ],
    },
    '/user/tasks/accept': {
      title: '??????',
      description: '??????????????????????????',
      missingPermission: 'task:accept',
      benefitPreview: ['???????', '??????', '??????'],
      upgradePaths: [
        { role: 'designer', path: '/auth/verify/designer', benefits: '??????', time: '?2??' },
      ],
    },
  }

  const checkAndShowGuidance = (path: string) => {
    const permissions = getFullPermissions(userStore)
    if (permissionService.hasPermission(path, permissions)) {
      return false
    }

    let guidance = guidanceConfig[path]
    if (!guidance) {
      const matchKey = Object.keys(guidanceConfig).find(key => path.startsWith(key))
      if (matchKey)
        guidance = guidanceConfig[matchKey]
    }

    if (guidance) {
      const hidden = localStorage.getItem(`permission-guidance-${guidance.missingPermission}`)
      if (hidden === 'hidden')
        return true

      currentGuidance.value = guidance
      showGuidanceDialog.value = true
      if (onDenied)
        onDenied()
      return true
    }

    return false
  }

  if (autoCheckRoute) {
    watch(
      () => route.path,
      (newPath) => {
        checkAndShowGuidance(newPath)
      },
    )
  }

  const handleUpgrade = (path: string) => {
    showGuidanceDialog.value = false
    router.push(path)
  }

  const handlePreviewMode = () => {
    showGuidanceDialog.value = false
  }

  const hideGuidance = () => {
    showGuidanceDialog.value = false
  }

  return {
    showGuidanceDialog,
    currentGuidance,
    checkAndShowGuidance,
    handleUpgrade,
    handlePreviewMode,
    hideGuidance,
  }
}
